import { useEffect, useState } from "react";
import { Box, Grid, styled } from "@mui/system";
import { Button, TablePagination, TextField, Tooltip } from "@mui/material";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import styles from "./Report.module.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import axios from "axios";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useNavigate } from "react-router-dom";
import {
  useLoaderContext,
  useToastContext,
} from "../../../Context/ToastContext";
import { useUserContext } from "../../../Context/UserContext";
import { Helmet } from "react-helmet";
import { BASE_URL } from "../../../config/config";
import Dropdown1 from "../Dropdown/Dropdown1";
import { apiGet } from "../../../utils/ApiManager";
import dayjs from "dayjs";

const settlementReport = process.env.REACT_APP_DOWNLOAD_SETTLEMENT_REPORT;
const dropdownViewBookings =
  process.env.REACT_APP_FETCH_RESTAURANTS_VIEW_BOOKING;
const settlementPayments = process.env.REACT_APP_SETTLEMENT_PAYMENTS;

const Report = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(100);
  const navigateTo = useNavigate();
  const { showToast } = useToastContext();
  const { getToken } = useUserContext();
  const { setLoading } = useLoaderContext();
  const [restaurantDropdown, setRestaurantDropdown] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [restaurantId, setRestaurantId] = useState("");
  const [error, setError] = useState({
    startDate: "",
    endDate: "",
  });
  // const [showDownloadExcel,setShowDownloadExcel] = useState(false)
  const [disabled, setDisabled] = useState(true);
  const [records, setRecords] = useState([]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const validateInputs = () => {
    let newError = {};
    setDisabled(true);
    if (startDate && dayjs(startDate)) {
      if (!dayjs(startDate).isValid()) {
        newError.startDate = "Please enter a valid date";
      }
    }
    if (endDate && dayjs(endDate)) {
      if (!dayjs(endDate).isValid()) {
        newError.endDate = "Please enter a valid date";
      }
    }

    if (startDate && endDate) {
      if (
        dayjs(startDate).isValid() &&
        dayjs(endDate) &&
        dayjs(startDate) > dayjs(endDate)
      ) {
        newError.endDate = "End date should be after start date";
      }
    }

    setError(newError);
    if (!Object.keys(newError).length) {
      if (startDate && endDate) {
        setDisabled(false);
      }
    }
  };

  const fetchDropdown = async () => {
    try {
      const response = await apiGet(
        `${dropdownViewBookings}?settlementTypes=true`
      );
      if (response?.success) {
        setRestaurantDropdown(response?.data?.restaurantList || []);
      } else {
        showToast(response?.message || "Error fetching dropdown.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message ||
          error?.message ||
          "Error fetching dropdown.",
        "error"
      );
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(1);
  };

  const handleDownload = async () => {
    try {
      const params = {
        startDate: startDate,
        endDate: endDate,
      };
      setLoading(true);
      const response = await axios.get(`${BASE_URL}${settlementReport}`, {
        responseType: "blob", // Important!
        params,
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        withCredentials: true,
      });
      if (response?.status === 200 || response?.status === 201) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        let fileName = `${dayjs(startDate).format("D MMMM")}_to_${dayjs(
          endDate
        ).format("D MMMM")}.xlsx`;
        link.setAttribute("download", fileName); // Use desired filename
        document.body.appendChild(link);
        link.click();
        link.remove();
        showToast("Report downloaded successfully.", "success");
      } else {
        showToast(
          response?.data?.message || "Error downloading report.",
          "error"
        );
      }

      // Create a Blob from the response
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (newSearch) => {
    try {
      setLoading(true);
      let params = {
        pageNo: page,
        pageSize: rowsPerPage,
      };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (restaurantId) params.restaurantId = restaurantId;
      const queryString = new URLSearchParams(params);
      const response = await apiGet(`${settlementPayments}?${queryString}`);
      if (response.success) {
        setRecords(response?.data || []);
        setTotalCount(response?.totalCount || 0);
      } else {
        showToast(response.message || "Error fetching records.", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message || error?.message || "Error fetching records.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const getWeekRangeTitle = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return "";

    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);

    const options = { day: "numeric", month: "short", year: "numeric" };

    const startFormatted = startDate
      .toLocaleDateString("en-US", options)
      .toUpperCase();
    const endFormatted = endDate
      .toLocaleDateString("en-US", options)
      .toUpperCase();

    return `REPORT OF ${startFormatted} TO ${endFormatted}`;
  };

  const handleDateChange = (value, name) => {
    const date = dayjs(value);
    if (name === "startDate" && date.isValid()) {
      setStartDate(date);
    } else if (name === "endDate" && date.isValid()) {
      setEndDate(date);
    }
  };

  const handleNextWeek = () => {
    if (startDate && endDate) {
      const nextStart = new Date(startDate);
      const nextEnd = new Date(endDate);

      nextStart.setDate(nextStart.getDate() + 7);
      nextEnd.setDate(nextEnd.getDate() + 7);

      setStartDate(nextStart.toISOString().split("T")[0]);
      setEndDate(nextEnd.toISOString().split("T")[0]);
    }
  };

  const handlePrevWeek = () => {
    if (startDate && endDate) {
      const prevStart = new Date(startDate);
      const prevEnd = new Date(endDate);

      prevStart.setDate(prevStart.getDate() - 7);
      prevEnd.setDate(prevEnd.getDate() - 7);

      setStartDate(prevStart.toISOString().split("T")[0]);
      setEndDate(prevEnd.toISOString().split("T")[0]);
    }
  };
  const getPrevWeekRange = () => {
    const today = new Date();

    // Get current day of week (0 = Sunday, 6 = Saturday)
    const dayOfWeek = today.getDay();

    // Calculate last Sunday's date (start of previous week)
    // Go back dayOfWeek + 7 days to get previous week's Sunday
    const prevSunday = new Date(today);
    prevSunday.setDate(today.getDate() - dayOfWeek - 7);

    // Previous week's Saturday = prevSunday + 6 days
    const prevSaturday = new Date(prevSunday);
    prevSaturday.setDate(prevSunday.getDate() + 6);

    // Format to yyyy-mm-dd for input[type=date]
    const formatDate = (date) => date.toISOString().split("T")[0];

    return {
      startDate: formatDate(prevSunday),
      endDate: formatDate(prevSaturday),
    };
  };

  const getGstAmount = (totalAmount, gstPercentage) => {
    let amount = (totalAmount * gstPercentage) / 100;
    return amount;
  };

  const getCommitionWithGst = (totalAmount, commissionPercentage) => {
    let commitionAmount = (totalAmount * commissionPercentage) / 100;
    let gstOnCommition = (commitionAmount * 18) / 100;
    let commitionwithGst = Number(commitionAmount + gstOnCommition).toFixed(2);
    return {
      commitionAmount,
      gstOnCommition,
      commitionwithGst,
    };
  };

  const getRestaurantPayout = (
    totalAmount,
    commissionPercentage,
    gstPercentageToDiner
  ) => {
    let commitionAmount =
      totalAmount +
      getGstAmount(totalAmount, gstPercentageToDiner) -
      getCommitionWithGst(totalAmount, commissionPercentage).commitionwithGst;
    return commitionAmount;
  };

  useEffect(() => {
    // const { startDate, endDate } = getPrevWeekRange();
    // setStartDate(startDate);
    // setEndDate(endDate);
    fetchDropdown();
  }, []);

  useEffect(() => {
    fetchRecords();
    validateInputs();
  }, [restaurantId, page, rowsPerPage]);
  useEffect(() => {
    if (dayjs(startDate).isValid() && dayjs(endDate).isValid()) {
      fetchRecords();
    }
    validateInputs();
  }, [startDate, endDate]);

  return (
    <Box className={styles.mainContainer}>
      <Helmet>
        <title>Payment Records | DiNEST</title>
        <meta name="description" content="View Payments on DiNEST" />
      </Helmet>
      <Box className={styles.restaurantListContainer}>
        <Typography variant="h4" className={styles.containerTitle}>
          Bill Payments
        </Typography>
        <Box className={styles.buttonContainer}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#207d46" }}
            onClick={handlePrevWeek}
          >
            Prev Week
          </Button>

          <Box className={styles.inputBox}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid className={styles.inputGroup}>
                <DesktopDatePicker
                  label="Start Date"
                  format="DD/MM/YYYY"
                  inputFormat="DD/MM/YYYY"
                  sx={{
                    backgroundColor: "#e8f2ec",
                    width: "100%",
                  }}
                  // className={styles.inputTextfield}
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(e) => handleDateChange(e, "startDate")}
                  renderInput={(params) => (
                    <TextField fullWidth size="small" {...params} />
                  )}
                />
                {error.startDate && (
                  <p className={styles.error}>{error.startDate}</p>
                )}
              </Grid>
              <Grid className={styles.inputGroup}>
                <DesktopDatePicker
                  label="End Date"
                  format="DD/MM/YYYY"
                  inputFormat="DD/MM/YYYY"
                  sx={{
                    backgroundColor: "#e8f2ec",
                    width: "100%",
                  }}
                  // className={styles.inputTextfield}
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(e) => handleDateChange(e, "endDate")}
                  renderInput={(params) => (
                    <TextField fullWidth size="small" {...params} />
                  )}
                />
                {error.endDate && (
                  <p className={styles.error}>{error.endDate}</p>
                )}
              </Grid>
            </LocalizationProvider>

            <Grid className={styles.inputGroup}>
              <Dropdown1
                label="Restaurant"
                options={restaurantDropdown}
                sx={{ width: "2rem" }}
                name="restauranId"
                onChange={(e) => setRestaurantId(e.target?.value)}
              />
            </Grid>
          </Box>

          <Button
            variant="contained"
            sx={{ backgroundColor: "#207d46" }}
            onClick={handleNextWeek}
          >
            Next Week
          </Button>
        </Box>

        <div className={styles.excelBtnContainer}>
          <Button
            onClick={() => navigateTo("./settlements")}
            variant="contained"
            sx={{ backgroundColor: "#207d46", justifyContent: "center" }}
          >
            View Settlements
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            disabled={disabled}
            sx={{ backgroundColor: "#207d46", justifyContent: "center" }}
          >
            Download Excel
          </Button>
        </div>
        {/* Search Bar */}
        {records?.length > 0 ? (
          <>
            {/* <div className={styles.searchBarContainer}>
              <TextField
                label="Search Restaurant by name"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchBar}
              />
            </div> */}

            <Typography
              variant="h5"
              textAlign="center"
              color="#207d46"
              fontWeight="bold"
              marginTop={5}
            >
              {getWeekRangeTitle(startDate, endDate)}
            </Typography>

            <TableContainer className={styles.tableContainer} component={Paper}>
              <Table aria-label="restaurant table">
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.tableHead}>Sr. No</TableCell>
                    <TableCell className={styles.tableHead}>
                      Booking ID
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Name
                    </TableCell>
                    <TableCell className={styles.tableHead}>Diner</TableCell>
                    <TableCell className={styles.tableHead}>
                      Booking Date & Time
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                    Sub Total Amount ( र )
                    </TableCell>
                    {/* <TableCell className={styles.tableHead}>CGST Amount</TableCell>
                    <TableCell className={styles.tableHead}>SGST Amount</TableCell> */}
                    <TableCell className={styles.tableHead}>
                    Sub Total GST (%) (C + S)gst
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                    Sub Total Amount (includes Gst)
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Dinest Commition ( र )
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      GST on Dinest Commiton ( 18% )
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Total Dinest Commission ( र )
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Restaurant Payout ( र )
                    </TableCell>
                    <TableCell className={styles.tableHead}>
                      Payment Settled ?{" "}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records &&
                    records?.map((record, index) => (
                      <TableRow key={record?.restaurantId}>
                        <TableCell className={styles.tableData}>
                          {rowsPerPage * (page - 1) + index + 1}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {record?.bookingId || "N/A"}
                        </TableCell>
                        <TableCell
                          className={styles.tableData}
                          sx={{ maxWidth: 150 }}
                        >
                          <Tooltip
                            title={record?.restaurantName || "N/A"}
                            arrow
                          >
                            <Typography
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 100,
                              }}
                            >
                              {record?.restaurantName || "N/A"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        <TableCell
                          className={styles.tableData}
                          sx={{ maxWidth: 150 }}
                        >
                          <Tooltip
                            title={
                              record?.bookedBy?.firstName
                                ? `${record?.bookedBy?.firstName} ${record?.bookedBy?.lastName}`
                                : "N/A"
                            }
                            arrow
                          >
                            <Typography
                              style={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 70,
                              }}
                            >
                              {record?.bookedBy?.firstName
                                ? `${record?.bookedBy?.firstName} ${record?.bookedBy?.lastName}`
                                : "N/A"}{" "}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {record?.bookingDate
                            ? new Date(record?.bookingDate).toLocaleDateString(
                                "en-GB"
                              )
                            : "N/A"}{" "}
                          {record?.startTime}
                        </TableCell>
                        {/* Total Amount Without GST */}
                        <TableCell className={styles.tableData}>
                          {record?.subTotalAmount - record?.discountAmount ||
                            "N/A"}
                        </TableCell>
                        {/* Cgst */}
                        {/* <TableCell className={styles.tableData}>
                          {record?.gstDetails?.cgst
                            ? getGstAmount(
                                record?.subTotalAmount - record?.discountAmount,
                                record?.gstDetails?.cgst
                              )
                            : "N/A"}
                        </TableCell> */}
                        {/* SGST */}
                        {/* <TableCell className={styles.tableData}>
                          {record?.gstDetails?.sgst
                            ? getGstAmount(
                                record?.subTotalAmount - record?.discountAmount,
                                record?.gstDetails?.sgst
                              )
                            : "N/A"}
                        </TableCell> */}
                        {/* Total GST (CSGT + SGST) */}
                        <TableCell className={styles.tableData}>
                          {getGstAmount(
                            record?.subTotalAmount - record?.discountAmount,
                            record?.gstDetails?.cgst + record?.gstDetails?.sgst
                          ) || "N/A"}
                        </TableCell>
                        {/* Total Paid (with GST) */}
                        <TableCell className={styles.tableData}>
                          {record?.amountPaid || "N/A"}
                        </TableCell>
                        {/* Commition without Gst */}
                        <TableCell className={styles.tableData}>
                          {record?.totalAmount
                            ? getCommitionWithGst(
                                record?.subTotalAmount - record?.discountAmount,
                                record?.commission
                              )?.commitionAmount
                            : "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {getCommitionWithGst(
                            record?.subTotalAmount - record?.discountAmount,
                            record?.commission
                          )?.gstOnCommition || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {getCommitionWithGst(
                            record?.subTotalAmount - record?.discountAmount,
                            record?.commission
                          )?.commitionwithGst || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {getRestaurantPayout(
                            record?.subTotalAmount - record?.discountAmount,
                            record?.commission,
                            record?.gstDetails?.cgst + record?.gstDetails?.sgst
                          ) || "N/A"}
                        </TableCell>
                        <TableCell className={styles.tableData}>
                          {record?.isSettled ? (
                            <DoneIcon sx={{ color: "#207d46" }} />
                          ) : (
                            <CloseIcon sx={{ color: "red" }} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div className={styles.paginationContainer}>
              <TablePagination
                className={`${styles.pagination}`}
                rowsPerPageOptions={[2, 5, 10, 15]}
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  "& .MuiTablePagination-actions button": {
                    backgroundColor: "transparent",
                    color: "white !important",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                    "&:focus": {
                      outline: "3px solid #2196F3 !important",
                    },
                  },
                  "& .MuiTablePagination-actions button svg": {
                    fill: "white !important",
                  },
                  "& .MuiSelect-root": {
                    color: "white !important",
                  },
                  "& .MuiSelect-icon": {
                    fill: "white !important",
                  },
                }}
                slotProps={{
                  select: {
                    "aria-label": "Rows per page",
                  },
                  actions: {
                    showFirstButton: true,
                    showLastButton: true,
                    slots: {
                      firstPageIcon: FirstPageRoundedIcon,
                      lastPageIcon: LastPageRoundedIcon,
                      nextPageIcon: ChevronRightRoundedIcon,
                      backPageIcon: ChevronLeftRoundedIcon,
                    },
                  },
                }}
              />
            </div>
          </>
        ) : (
          <h2 style={{ color: "red", textAlign: "center", marginTop: "2rem" }}>
            No payment records found !
          </h2>
        )}
      </Box>
    </Box>
  );
};

const StatusLabel = styled("span")(({ active }) => ({
  color: active ? "#4CAF50" : "#f44336",
  fontWeight: "bold",
  textTransform: "uppercase",
}));

export default Report;
